import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useWcNews } from '@/features/news/hooks';

import { NewsCard } from './NewsCard';

/**
 * The competition-wide World Cup news list (no fixture filter): the body of the
 * News screen's "World Cup" tab. Owns its own loading / error / empty states so
 * the screen just mounts it. Pulls via {@link useWcNews}.
 */
export function WcNewsSection({ limit = 30 }: { limit?: number }) {
  const { t } = useTranslation();
  const query = useWcNews(undefined, limit);
  const articles = query.data ?? [];

  if (query.isPending) {
    return (
      <View className="gap-3 px-4">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-44 w-full" />
        ))}
      </View>
    );
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  }
  if (articles.length === 0) {
    return (
      <EmptyState
        icon="newspaper-outline"
        title={t('news.wcEmptyTitle')}
        body={t('news.wcEmptyBody')}
      />
    );
  }
  return (
    <View className="gap-3 px-4">
      {articles.map((article) => (
        <NewsCard key={article.id} article={article} />
      ))}
    </View>
  );
}
